import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/button';
import type { AppLanguage } from '../../lib/localization';

export interface CancelDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void | Promise<void>;
  language?: AppLanguage;
  documentLabel?: string;
  pending?: boolean;
}

const COPY = {
  en: {
    title: 'Cancel document',
    description: 'This action may reverse accounting or stock effects. Enter a reason to continue.',
    reason: 'Cancellation reason',
    placeholder: 'Explain why this document is being cancelled…',
    required: 'A cancellation reason is required.',
    close: 'Close dialog',
    keep: 'Keep document',
    confirm: 'Cancel document',
    confirming: 'Cancelling…',
  },
  ar: {
    title: 'إلغاء المستند',
    description: 'قد يعكس هذا الإجراء الآثار المحاسبية أو المخزنية. أدخل سبباً للمتابعة.',
    reason: 'سبب الإلغاء',
    placeholder: 'وضّح سبب إلغاء هذا المستند…',
    required: 'سبب الإلغاء مطلوب.',
    close: 'إغلاق مربع الحوار',
    keep: 'الاحتفاظ بالمستند',
    confirm: 'إلغاء المستند',
    confirming: 'جارٍ الإلغاء…',
  },
} as const;

export function CancelDocumentDialog({
  open,
  onOpenChange,
  onConfirm,
  language = 'en',
  documentLabel,
  pending = false,
}: CancelDocumentDialogProps) {
  const [reason, setReason] = useState('');
  const [validationError, setValidationError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const errorId = useId();
  const copy = COPY[language];
  const isBusy = pending || submitting;

  useEffect(() => {
    if (!open) {
      setReason('');
      setValidationError('');
      setSubmitting(false);
      return;
    }

    const frame = window.requestAnimationFrame(() => textareaRef.current?.focus());
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isBusy) onOpenChange(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, isBusy, onOpenChange]);

  if (!open) return null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setValidationError(copy.required);
      textareaRef.current?.focus();
      return;
    }

    setValidationError('');
    setSubmitting(true);
    try {
      await onConfirm(trimmedReason);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="ui-dialog-overlay"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isBusy) onOpenChange(false);
      }}
    >
      <form className="ui-dialog-content" onSubmit={handleSubmit} noValidate>
        <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-100 text-rose-600 dark:bg-rose-950/60 dark:text-rose-300">
              <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <h2 id={titleId} className="text-base font-semibold tracking-tight">
                {documentLabel ? `${copy.title}: ${documentLabel}` : copy.title}
              </h2>
              <p id={descriptionId} className="mt-1 text-sm leading-5 text-muted-foreground">
                {copy.description}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            disabled={isBusy}
            aria-label={copy.close}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-2 px-6 py-5">
          <label htmlFor={`${titleId}-reason`} className="block text-sm font-medium text-foreground">
            {copy.reason} <span className="text-destructive" aria-hidden="true">*</span>
          </label>
          <textarea
            ref={textareaRef}
            id={`${titleId}-reason`}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              if (validationError && event.target.value.trim()) setValidationError('');
            }}
            rows={4}
            required
            disabled={isBusy}
            placeholder={copy.placeholder}
            aria-invalid={Boolean(validationError)}
            aria-describedby={validationError ? errorId : descriptionId}
            className="flex min-h-24 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm outline-none transition-[border-color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-destructive/20"
          />
          {validationError && <p id={errorId} role="alert" className="text-sm text-destructive">{validationError}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
            {copy.keep}
          </Button>
          <Button type="submit" variant="destructive" disabled={isBusy || !reason.trim()}>
            {isBusy ? copy.confirming : copy.confirm}
          </Button>
        </div>
      </form>
    </div>
  );
}
